import React from 'react';
import { useOverviewDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/store/overviewDashboard.store';
import { TrendGranularity } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.types';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import {
  VerticalBarChart,
  VerticalBarItem,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/VerticalBarChart';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import {
  TrendLegend,
  TrendLegendItem,
  TrendLegendDot,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/FulfillmentTrendChart.styled';

function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatTrendLabel(dateStr: string, granularity: TrendGranularity): string {
  const date = parseLocalDate(dateStr);
  if (granularity === 'monthly') {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${month} '${String(date.getFullYear()).slice(-2)}`;
  }
  const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (granularity === 'weekly') return monthDay;
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  return `${weekday} ${monthDay}`;
}

function formatTrendAxisLabel(dateStr: string, granularity: TrendGranularity): string {
  if (granularity !== 'daily') return formatTrendLabel(dateStr, granularity);
  const date = parseLocalDate(dateStr);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${weekday}\n${monthDay}`;
}

export const FulfillmentTrendChart: React.FC = () => {
  const trendData = useOverviewDashboardStore((s) => s.trendData);
  const trendGranularity = useOverviewDashboardStore((s) => s.trendGranularity);
  const palette = useChartColors();

  if (trendData.length === 0) {
    return (
      <EmptyStateContainer>No trend data available for the selected filters.</EmptyStateContainer>
    );
  }

  if (trendGranularity === null) {
    return (
      <EmptyStateContainer>
        Trend data is unavailable because the data granularity is missing.
      </EmptyStateContainer>
    );
  }

  const totalShiftsColor = palette.chartColors[0];
  const fulfilledColor = palette.chartColors[7] || '#A390D4';

  const items: VerticalBarItem[] = trendData.map((d) => {
    const fulfilledCount = Math.round((d.totalShifts * d.fulfillmentPercentage) / 100);
    const fulfillmentPct = Math.round(d.fulfillmentPercentage);
    const label = formatTrendLabel(d.date, trendGranularity);
    return {
      key: d.date,
      value: d.totalShifts,
      ariaLabel: `${label}: ${d.totalShifts} total shifts, ${fulfilledCount} fulfilled (${fulfillmentPct}%)`,
      tooltipHtml: `<strong>${label}</strong><br/>Total shifts: ${d.totalShifts.toLocaleString()}<br/>Fulfilled: ${fulfilledCount.toLocaleString()} (${fulfillmentPct}%)`,
      barColor: totalShiftsColor,
      aboveLabels: [
        { text: `(${fulfillmentPct}%)`, color: fulfilledColor },
        { text: fulfilledCount.toLocaleString(), color: fulfilledColor },
      ],
      subLabels: [{ text: d.totalShifts.toLocaleString(), color: totalShiftsColor }],
    };
  });

  return (
    <>
      <TrendLegend>
        <TrendLegendItem>
          <TrendLegendDot $color={totalShiftsColor} />
          <span>Total shifts</span>
        </TrendLegendItem>
        <TrendLegendItem>
          <TrendLegendDot $color={fulfilledColor} />
          <span>Fulfilled shifts (count &amp; rate)</span>
        </TrendLegendItem>
      </TrendLegend>
      <VerticalBarChart
        items={items}
        height={220}
        xTickFormat={(key) => formatTrendAxisLabel(key, trendGranularity)}
        ariaLabel="Fulfillment trend chart showing total shifts and fulfillment percentage over time"
      />
    </>
  );
};
